import styled from "@emotion/styled";

import { formatClassSchedule } from "@shared/lib/utils/date/formatSchedule";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

import {
  CourseTypeEnum,
  ScheduleResponseDto,
  ScheduleStatus,
} from "@shared/generated/api/model";

interface ScheduleListProps {
  schedules: ScheduleResponseDto[];
  onLectureClick: (courseId: number) => void;
  onManageClick: (courseId: number) => void;
  onScheduleManageClick: (scheduleId: number) => void;
}

export const ScheduleList = ({
  schedules,
  onLectureClick,
  onManageClick,
  onScheduleManageClick,
}: ScheduleListProps) => {
  const { t } = useCustomTranslation();
  const getStatusDisplay = (status: ScheduleStatus) => {
    const statusMap = {
      [ScheduleStatus.PENDING]: t("waiting_to_join_the_class"),
      [ScheduleStatus.MEETING_READY]: t("entry_possible"),
      [ScheduleStatus.ONGOING]: t("in_progress"),
      [ScheduleStatus.COMPLETED]: t("completed"),
      [ScheduleStatus.CANCELLED]: t("canceled"),
      [ScheduleStatus.EXPIRED]: t("expired"),
      [ScheduleStatus.READY_TO_COMPLETE]: t("waiting_for_completion"),
    };
    return statusMap[status];
  };
  if (schedules.length === 0) {
    return <EmptySchedule>{t("no_registered_schedules")}</EmptySchedule>;
  }

  return (
    <Container>
      {schedules.map((schedule) => {
        const { course } = schedule.cycle;

        return (
          <ScheduleItem key={schedule.id}>
            <Header>
              <CourseInfo>
                <CourseType>
                  {course.type === CourseTypeEnum.SINGLE
                    ? t("one_day")
                    : t("multiple_sessions")}
                  /{course.difficulty}
                </CourseType>
                <CourseTitle onClick={() => onLectureClick(course.id)}>
                  {course.title}
                </CourseTitle>
              </CourseInfo>
              <ButtonGroup>
                <Button onClick={() => onManageClick(course.id)}>
                  {t("class_management")}
                </Button>
                <Button onClick={() => onScheduleManageClick(schedule.id)}>
                  {t("schedule_management")}
                </Button>
              </ButtonGroup>
            </Header>

            <Content>
              <DateTimeInfo>
                {formatClassSchedule(
                  new Date(schedule.startsAt),
                  new Date(schedule.endsAt),
                  schedule.cycle.course.durationPerSession,
                )}
                {course.type === CourseTypeEnum.MULTI && (
                  <SessionInfo>
                    {schedule.sessionNumber}/
                    {schedule.cycle.course.totalSessions}
                    {t("session")}
                  </SessionInfo>
                )}
              </DateTimeInfo>

              <StatusBadge status={schedule.status}>
                {getStatusDisplay(schedule.status)}
              </StatusBadge>

              {schedule.status === ScheduleStatus.PENDING && (
                <EnrollmentInfo>
                  {/* TODO: 실제 등록된 학생 수를 가져와야 함 */}
                  <RemainingSeats>
                    {schedule.cycle.maxEnrollments}
                    {t("spots_remaining")}
                  </RemainingSeats>
                  {/* TODO: 실제 학생 목록을 가져와야 함 */}
                  <StudentList>홍길동, 김철수, 이영희</StudentList>
                </EnrollmentInfo>
              )}
            </Content>
          </ScheduleItem>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

const EmptySchedule = styled.div`
  padding: 24px;
  text-align: center;
  color: #6b7280;
`;

const ScheduleItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const CourseInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CourseType = styled.span`
  font-size: 14px;
  color: #6b7280;
`;

const CourseTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;

  &:hover {
    color: #2563eb;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: white;
  font-size: 14px;
  color: #374151;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DateTimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
`;

const SessionInfo = styled.span`
  color: #6b7280;
  &::before {
    content: "•";
    margin: 0 4px;
  }
`;

const StatusBadge = styled.span<{ status: ScheduleStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;

  ${({ status }) => {
    switch (status) {
      case ScheduleStatus.PENDING:
        return `
         background: #EFF6FF;
         color: #2563EB;
       `;
      case ScheduleStatus.MEETING_READY:
      case ScheduleStatus.ONGOING:
        return `
         background: #ECFDF5;
         color: #059669;
       `;
      case ScheduleStatus.COMPLETED:
      case ScheduleStatus.READY_TO_COMPLETE:
        return `
         background: #F3F4F6;
         color: #374151;
       `;
      case ScheduleStatus.CANCELLED:
      case ScheduleStatus.EXPIRED:
        return `
         background: #FEF2F2;
         color: #DC2626;
       `;
    }
  }}
`;

const EnrollmentInfo = styled.div`
  margin-top: 8px;
  font-size: 14px;
`;

const RemainingSeats = styled.div`
  color: #2563eb;
  margin-bottom: 4px;
`;

const StudentList = styled.div`
  color: #6b7280;
`;

